import { type Static, Type } from "@sinclair/typebox"
import { AuthoredCardSchema } from "./cards"
import { IdSchema, IsoTimestampSchema, NonEmptyStringSchema } from "./common"
import { HeroDefinitionSchema } from "./heroes"
import { PlayerRoleSchema } from "./profiles"

export const ChallengerSeatInputSchema = Type.Object({
  seatId: Type.Union([Type.Literal("seat_a"), Type.Literal("seat_b")]),
  playerId: NonEmptyStringSchema,
  displayName: NonEmptyStringSchema,
  hero: HeroDefinitionSchema,
  mercCards: Type.Array(AuthoredCardSchema),
})

export type ChallengerSeatInput = Static<typeof ChallengerSeatInputSchema>

export const PublicPlayerProfileSchema = Type.Object({
  ownerUserId: IdSchema,
  gamerTag: NonEmptyStringSchema,
  gamerTagNormalized: NonEmptyStringSchema,
  avatarUrl: Type.Optional(Type.String({ minLength: 1 })),
  friendCode: Type.Optional(NonEmptyStringSchema),
  role: PlayerRoleSchema,
  createdAt: IsoTimestampSchema,
  updatedAt: IsoTimestampSchema,
})

export type PublicPlayerProfile = Static<typeof PublicPlayerProfileSchema>

export const PublicPlayerProfileSyncEnvelopeSchema = Type.Object({
  record: PublicPlayerProfileSchema,
})

export type PublicPlayerProfileSyncEnvelope = Static<
  typeof PublicPlayerProfileSyncEnvelopeSchema
>

export const PublicPlayerProfileSyncResponseSchema =
  PublicPlayerProfileSyncEnvelopeSchema

export type PublicPlayerProfileSyncResponse = Static<
  typeof PublicPlayerProfileSyncResponseSchema
>

export const PublicPlayerProfileListSchema = Type.Array(
  PublicPlayerProfileSchema,
  {
    maxItems: 500,
  },
)

export type PublicPlayerProfileList = Static<
  typeof PublicPlayerProfileListSchema
>

export const PublicPlayerProfileListResponseSchema = Type.Object({
  records: PublicPlayerProfileListSchema,
})

export type PublicPlayerProfileListResponse = Static<
  typeof PublicPlayerProfileListResponseSchema
>

export const FriendCodeLookupResultSchema = Type.Object({
  ownerUserId: IdSchema,
  gamerTag: NonEmptyStringSchema,
  gamerTagNormalized: NonEmptyStringSchema,
})

export type FriendCodeLookupResult = Static<typeof FriendCodeLookupResultSchema>

export const FriendCodeLookupEnvelopeSchema = Type.Object({
  record: FriendCodeLookupResultSchema,
})

export type FriendCodeLookupEnvelope = Static<
  typeof FriendCodeLookupEnvelopeSchema
>

export const ChallengeStatusSchema = Type.Union([
  Type.Literal("pending"),
  Type.Literal("accepted"),
  Type.Literal("declined"),
  Type.Literal("cancelled"),
])

export type ChallengeStatus = Static<typeof ChallengeStatusSchema>

export const ManagedChallengeRecordSchema = Type.Object({
  challengeId: IdSchema,
  challengerTag: NonEmptyStringSchema,
  challengerUserId: Type.Optional(IdSchema),
  challengedTag: NonEmptyStringSchema,
  challengedUserId: Type.Optional(IdSchema),
  challengerDeckId: NonEmptyStringSchema,
  challengerSeatInput: Type.Optional(ChallengerSeatInputSchema),
  challengedDeckId: Type.Optional(NonEmptyStringSchema),
  matchId: Type.Optional(NonEmptyStringSchema),
  status: ChallengeStatusSchema,
  createdAt: IsoTimestampSchema,
  remoteSyncConflictAt: Type.Optional(IsoTimestampSchema),
  remoteSyncFailedAt: Type.Optional(IsoTimestampSchema),
  remoteSyncedAt: Type.Optional(IsoTimestampSchema),
  updatedAt: IsoTimestampSchema,
})

export type ManagedChallengeRecord = Static<typeof ManagedChallengeRecordSchema>

export const ManagedChallengeSyncEnvelopeSchema = Type.Object({
  record: ManagedChallengeRecordSchema,
})

export type ManagedChallengeSyncEnvelope = Static<
  typeof ManagedChallengeSyncEnvelopeSchema
>

export const ManagedChallengeSyncResponseSchema =
  ManagedChallengeSyncEnvelopeSchema

export type ManagedChallengeSyncResponse = Static<
  typeof ManagedChallengeSyncResponseSchema
>

export const ChallengeRecordListSchema = Type.Array(
  ManagedChallengeRecordSchema,
  {
    maxItems: 2000,
  },
)

export type ChallengeRecordList = Static<typeof ChallengeRecordListSchema>

export const ChallengeListResponseSchema = Type.Object({
  records: ChallengeRecordListSchema,
})

export type ChallengeListResponse = Static<typeof ChallengeListResponseSchema>

export const FriendRequestStatusSchema = Type.Union([
  Type.Literal("pending"),
  Type.Literal("accepted"),
  Type.Literal("declined"),
  Type.Literal("cancelled"),
])

export type FriendRequestStatus = Static<typeof FriendRequestStatusSchema>

export const ManagedFriendRequestRecordSchema = Type.Object({
  requestId: IdSchema,
  inviterUserId: IdSchema,
  inviterTag: NonEmptyStringSchema,
  requesterUserId: IdSchema,
  requesterTag: NonEmptyStringSchema,
  status: FriendRequestStatusSchema,
  createdAt: IsoTimestampSchema,
  remoteSyncConflictAt: Type.Optional(IsoTimestampSchema),
  remoteSyncFailedAt: Type.Optional(IsoTimestampSchema),
  remoteSyncedAt: Type.Optional(IsoTimestampSchema),
  updatedAt: IsoTimestampSchema,
})

export type ManagedFriendRequestRecord = Static<
  typeof ManagedFriendRequestRecordSchema
>

export const ManagedFriendRequestSyncEnvelopeSchema = Type.Object({
  record: ManagedFriendRequestRecordSchema,
})

export type ManagedFriendRequestSyncEnvelope = Static<
  typeof ManagedFriendRequestSyncEnvelopeSchema
>

export const ManagedFriendRequestSyncResponseSchema =
  ManagedFriendRequestSyncEnvelopeSchema

export type ManagedFriendRequestSyncResponse = Static<
  typeof ManagedFriendRequestSyncResponseSchema
>

export const FriendRequestRecordListSchema = Type.Array(
  ManagedFriendRequestRecordSchema,
  {
    maxItems: 2000,
  },
)

export type FriendRequestRecordList = Static<
  typeof FriendRequestRecordListSchema
>

export const FriendRequestListResponseSchema = Type.Object({
  records: FriendRequestRecordListSchema,
})

export type FriendRequestListResponse = Static<
  typeof FriendRequestListResponseSchema
>
